import { el } from '../dom.js';

const TRAILER_ID  = 'vPwE9kdwfSM';
const TRAILER_URL = `https://www.youtube.com/watch?v=${TRAILER_ID}`;
// YouTube no longer lets an embed drop the title bar (`showinfo` was retired
// in 2018 and `modestbranding` is now a no-op), so this asks for as little
// chrome as is still on offer: no annotations, related videos limited to the
// same channel, and the fullscreen button explicitly on.
const TRAILER_EMBED = `https://www.youtube-nocookie.com/embed/${TRAILER_ID}?` + new URLSearchParams({
  autoplay: 1,
  rel: 0,
  iv_load_policy: 3,
  modestbranding: 1,
  playsinline: 1,
  fs: 1,
});

/**
 * Best-effort "this is a phone or tablet" test. Only used to set expectations,
 * never to block — a tablet with a mouse and keyboard can still fly — so a
 * false positive costs one line of text. iPadOS reports itself as desktop
 * Safari, hence the touch-point check on a "Mac".
 */
function isMobileDevice() {
  if (navigator.userAgentData?.mobile) return true;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile|Silk|Kindle|BlackBerry|Opera Mini|IEMobile/i.test(ua)) return true;
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true;
  return window.matchMedia?.('(pointer: coarse) and (hover: none)').matches ?? false;
}

/**
 * Fullscreen, opaque loading screen. Shows a progress bar while assets stream
 * in, then swaps it for an "Enter" button once everything is ready.
 */
export default class LoadingScreen {
  constructor({ onEnter }) {
    this.onEnter = onEnter;

    this.el = el('div', 'screen loading-screen');

    const box = el('div', 'loading-box');
    // Wordmark: a masked <div> rather than an <img>, so CSS owns the tint, and
    // split in two so the drop shadow sits outside the mask (see _menu.scss).
    const logo = el('div', 'loading-logo');
    logo.appendChild(el('div', 'loading-logo__mark'));
    box.appendChild(logo);

    this._track = el('div', 'loading-progress');
    this._bar = el('div', 'loading-bar');
    this._track.appendChild(this._bar);
    box.appendChild(this._track);

    this._status = el('div', 'loading-status', 'Loading assets…');
    box.appendChild(this._status);

    this._enter = el('button', 'loading-enter', '>> ENTER <<');
    this._enter.type = 'button';
    this._enter.hidden = true;
    this._enter.addEventListener('click', () => this.onEnter?.());
    box.appendChild(this._enter);

    if (isMobileDevice()) box.appendChild(this._buildMobileNotice());

    this.el.appendChild(box);
  }

  _buildMobileNotice() {
    const notice = el('div', 'loading-mobile');
    notice.appendChild(el('p', 'loading-mobile__title', 'Optimized for desktop'));
    notice.appendChild(el('p', 'loading-mobile__body',
      'Desktop recommended - mouse and keyboard are required to fly.'));
    const link = el('a', 'loading-mobile__link', '▶ Watch the trailer');
    link.href = TRAILER_URL;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    // Plain click opens the lightbox; the href stays as the fallback for
    // middle-click / open-in-new-tab.
    link.addEventListener('click', (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      this._openTrailer();
    });
    notice.appendChild(link);
    return notice;
  }

  /**
   * Trailer lightbox. The iframe is created on open and destroyed on close,
   * which is what stops playback — and means nothing is fetched from YouTube
   * unless the player asks for it.
   */
  _openTrailer() {
    if (this._lightbox) return;

    const box = el('div', 'trailer-lightbox');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Gameplay trailer');

    const frameWrap = el('div', 'trailer-lightbox__frame');
    const iframe = document.createElement('iframe');
    // Every attribute goes on before `src`: the frame's permissions policy is
    // fixed when it navigates, so a late `allow` would leave the player's own
    // fullscreen button greyed out.
    // The dev server sends COEP: require-corp, which blocks a cross-origin
    // iframe outright; `credentialless` is how Chrome lets one load under it.
    iframe.setAttribute('credentialless', '');
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
    iframe.setAttribute('allowfullscreen', '');
    iframe.title = 'Gameplay trailer';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.src = TRAILER_EMBED;
    frameWrap.appendChild(iframe);
    box.appendChild(frameWrap);

    const close = el('button', 'trailer-lightbox__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close trailer');
    close.addEventListener('click', () => this._closeTrailer());
    box.appendChild(close);

    // Backdrop click closes; clicks on the video itself land in the iframe.
    box.addEventListener('click', (e) => { if (e.target === box) this._closeTrailer(); });
    this._onTrailerKey = (e) => { if (e.key === 'Escape') this._closeTrailer(); };
    window.addEventListener('keydown', this._onTrailerKey);

    this._lightbox = box;
    this.el.appendChild(box);
    close.focus();
  }

  _closeTrailer() {
    if (!this._lightbox) return;
    window.removeEventListener('keydown', this._onTrailerKey);
    this._lightbox.remove();
    this._lightbox = null;
  }

  setProgress(loaded, total) {
    const pct = total > 0 ? loaded / total : 0;
    this._bar.style.width = `${(pct * 100).toFixed(0)}%`;
    this._status.textContent = `Loading assets… ${(pct * 100).toFixed(0)}%`;
  }

  /** Assets finished loading — reveal the Enter button. */
  ready() {
    this._track.hidden = true;
    this._status.hidden = true;
    this._enter.hidden = false;
  }

  setVisible(v) {
    if (!v) this._closeTrailer();
    this.el.classList.toggle('is-hidden', !v);
  }
}
